import React, { useState, useEffect } from 'react';
import styled from 'styled-components';

const ConversationContainer = styled.div`
  background-color: #0a0a0a;
  border-radius: 8px;
  padding: 20px;
  color: white;
  max-height: 500px;
  overflow-y: auto;
`;

const GuideAvatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, #d4af37, #f5d76e);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 12px;
  font-weight: bold;
  color: black;
  font-size: 18px;
  flex-shrink: 0;
`;

const MessageBubble = styled.div`
  display: flex;
  align-items: flex-start;
  margin-bottom: 15px;
  
  ${props => props.isUser && `
    flex-direction: row-reverse;
    .message-content {
      background-color: #D4AF37;
      color: black;
      margin-left: 12px;
      margin-right: 0;
    }
  `}
`;

const MessageContent = styled.div`
  background-color: #1a1a1a;
  padding: 12px 16px;
  border-radius: 12px;
  margin-right: 12px;
  max-width: 70%;
  line-height: 1.4;
`;

const OptionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 10px;
  margin-top: 15px;
`;

const OptionCard = styled.button`
  background-color: #222;
  border: 2px solid #333;
  border-radius: 8px;
  padding: 15px;
  color: white;
  cursor: pointer;
  transition: all 0.3s ease;
  text-align: left;
  
  &:hover {
    border-color: #D4AF37;
    background-color: #2a2a2a;
  }
  
  &:active {
    transform: scale(0.98);
  }
`;

const OptionTitle = styled.div`
  font-weight: bold;
  color: #D4AF37;
  margin-bottom: 5px;
  font-size: 16px;
`;

const OptionDescription = styled.div`
  font-size: 14px;
  color: #ccc;
  line-height: 1.3;
`;

const VendorCard = styled(OptionCard)`
  border-color: #D4AF37;
  background-color: #1a1a1a;
  
  &:hover {
    background-color: #D4AF37;
    color: black;
    
    ${OptionTitle} {
      color: black;
    }
    
    ${OptionDescription} {
      color: #333;
    }
  }
`;

const BackButton = styled.button`
  background-color: #333;
  border: 1px solid #555;
  color: white;
  padding: 8px 16px;
  border-radius: 6px;
  cursor: pointer;
  margin-top: 15px;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: #444;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 15px;
  flex-wrap: wrap;
`;

const ActionButton = styled.button`
  background-color: #D4AF37;
  border: none;
  color: black;
  padding: 10px 20px;
  border-radius: 6px;
  cursor: pointer;
  font-weight: bold;
  transition: all 0.2s;
  
  &:hover {
    background-color: #B8860B;
  }
`;

const CancelButton = styled(ActionButton)`
  background-color: #666;
  color: white;
  
  &:hover {
    background-color: #777;
  }
`;

const FestivalGuideConversation = ({ onSelectItem, onCancel }) => {
  const [conversation, setConversation] = useState([]);
  const [currentStep, setCurrentStep] = useState('welcome');
  const [selectedCategory, setSelectedCategory] = useState(null);

  // Festival categories with vendors
  const festivalData = {
    drinks: {
      title: "Beverages & Cocktails",
      description: "Quench your thirst with our amazing drink selection",
      vendors: [
        {
          name: "Craft Cocktail Corner",
          description: "Premium cocktails made with top-shelf spirits",
          specialties: ["Old Fashioned", "Margarita", "Whiskey Sour"],
          type: "cocktails"
        },
        {
          name: "Local Brew Station",
          description: "Featuring local craft beers and ciders",
          specialties: ["IPA", "Lager", "Craft Cider"],
          type: "beer"
        },
        {
          name: "Wine & Spirits Bar",
          description: "Curated selection of wines and premium spirits",
          specialties: ["Pinot Noir", "Chardonnay", "Premium Whiskey"],
          type: "wine"
        },
        {
          name: "Fresh Juice Bar",
          description: "Cold-pressed juices and healthy smoothies",
          specialties: ["Green Juice", "Fruit Smoothies", "Kombucha"],
          type: "non_alcoholic"
        }
      ]
    },
    food: {
      title: "Gourmet Food",
      description: "Delicious eats from local chefs and food trucks",
      vendors: [
        {
          name: "BBQ Smoke House",
          description: "Authentic smoked meats and southern sides",
          specialties: ["Pulled Pork", "Brisket", "Ribs"],
          type: "bbq"
        },
        {
          name: "Gourmet Burger Station",
          description: "Artisan burgers with premium ingredients",
          specialties: ["Truffle Burger", "Veggie Burger", "Classic Cheeseburger"],
          type: "burgers"
        },
        {
          name: "Taco Fiesta",
          description: "Authentic Mexican street food",
          specialties: ["Fish Tacos", "Carnitas", "Vegetarian Tacos"],
          type: "mexican"
        },
        {
          name: "Pizza Wood Fire",
          description: "Neapolitan-style wood-fired pizzas",
          specialties: ["Margherita", "Pepperoni", "Prosciutto"],
          type: "pizza"
        }
      ]
    },
    sweets: {
      title: "Sweet Treats",
      description: "Indulge in our delectable desserts and confections",
      vendors: [
        {
          name: "Artisan Ice Cream",
          description: "Small-batch ice cream with unique flavors",
          specialties: ["Lavender Honey", "Salted Caramel", "Chocolate Chip"],
          type: "ice_cream"
        },
        {
          name: "Gourmet Donut Shop",
          description: "Hand-crafted donuts with creative toppings",
          specialties: ["Maple Bacon", "Glazed Old Fashioned", "Fruity Pebbles"],
          type: "donuts"
        },
        {
          name: "Cotton Candy Dreams",
          description: "Fresh spun cotton candy in amazing flavors",
          specialties: ["Blue Raspberry", "Pink Vanilla", "Grape"],
          type: "cotton_candy"
        },
        {
          name: "Chocolate Paradise",
          description: "Handmade chocolates and truffles",
          specialties: ["Dark Chocolate Truffles", "Milk Chocolate", "White Chocolate"],
          type: "chocolate"
        }
      ]
    },
    snacks: {
      title: "Festival Snacks",
      description: "Quick bites and festival favorites",
      vendors: [
        {
          name: "Kettle Corn Kingdom",
          description: "Fresh popped kettle corn and flavored popcorn",
          specialties: ["Classic Kettle Corn", "Caramel Corn", "Cheese Popcorn"],
          type: "popcorn"
        },
        {
          name: "Pretzel Paradise",
          description: "Warm soft pretzels with various toppings",
          specialties: ["Salted Pretzel", "Cinnamon Sugar", "Cheese Pretzel"],
          type: "pretzels"
        },
        {
          name: "Fruit Fresh Stand",
          description: "Fresh cut fruits and healthy snacks",
          specialties: ["Fruit Cups", "Smoothie Bowls", "Dried Fruits"],
          type: "fruit"
        }
      ]
    }
  };

  useEffect(() => {
    // Start the conversation
    setConversation([
      {
        sender: 'guide',
        message: "Hey there! 🎪 Welcome to the festival! I'm your personal festival guide. I'm here to help you discover all the amazing food, drinks, and treats we have to offer. What sounds good to you right now?"
      }
    ]);
  }, []);

  const addMessage = (sender, message) => {
    setConversation(prev => [...prev, { sender, message }]);
  };

  const handleCategorySelect = (categoryKey) => {
    const category = festivalData[categoryKey];
    setSelectedCategory(categoryKey);
    setCurrentStep('vendors');
    
    addMessage('user', `I'm interested in ${category.title.toLowerCase()}`);
    addMessage('guide', `Great choice! We have some amazing ${category.title.toLowerCase()} vendors here at the festival. Each one offers something special. Which one catches your eye?`);
  };

  const handleVendorSelect = (vendor) => {
    addMessage('user', `Tell me more about ${vendor.name}`);
    addMessage('guide', `${vendor.name} is fantastic! ${vendor.description} They're known for their ${vendor.specialties.join(', ')}. Would you like to see their menu?`);
    setCurrentStep('vendor_selected');
  };

  const handleViewMenu = () => {
    // This would trigger showing the menu for the selected vendor type
    const vendor = festivalData[selectedCategory];
    onSelectItem({
      name: `${vendor.title} Menu`,
      type: selectedCategory,
      action: 'show_menu'
    });
  };

  const handleStartOver = () => {
    setCurrentStep('welcome');
    setSelectedCategory(null);
    setConversation([
      {
        sender: 'guide',
        message: "No problem! Let's start fresh. What type of festival experience are you looking for today?"
      }
    ]);
  };

  const renderCurrentOptions = () => {
    if (currentStep === 'welcome') {
      return (
        <OptionsGrid>
          {Object.entries(festivalData).map(([key, category]) => (
            <OptionCard key={key} onClick={() => handleCategorySelect(key)}>
              <OptionTitle>{category.title}</OptionTitle>
              <OptionDescription>{category.description}</OptionDescription>
            </OptionCard>
          ))}
        </OptionsGrid>
      );
    }

    if (currentStep === 'vendors' && selectedCategory) {
      const category = festivalData[selectedCategory];
      return (
        <>
          <OptionsGrid>
            {category.vendors.map((vendor, index) => (
              <VendorCard key={index} onClick={() => handleVendorSelect(vendor)}>
                <OptionTitle>{vendor.name}</OptionTitle>
                <OptionDescription>{vendor.description}</OptionDescription>
                <div style={{ marginTop: '8px', fontSize: '12px', color: '#D4AF37' }}>
                  Specialties: {vendor.specialties.join(', ')}
                </div>
              </VendorCard>
            ))}
          </OptionsGrid>
          <BackButton onClick={handleStartOver}>
            ← Back to Categories
          </BackButton>
        </>
      );
    }

    if (currentStep === 'vendor_selected') {
      return (
        <ActionButtons>
          <ActionButton onClick={handleViewMenu}>
            View Menu
          </ActionButton>
          <ActionButton onClick={() => setCurrentStep('vendors')}>
            See Other Vendors
          </ActionButton>
          <ActionButton onClick={handleStartOver}>
            Browse Other Categories
          </ActionButton>
        </ActionButtons>
      );
    }

    return null;
  };

  return (
    <ConversationContainer>
      <div style={{ marginBottom: '20px', textAlign: 'center' }}>
        <h3 style={{ color: '#D4AF37', margin: '0 0 5px 0' }}>🎪 Festival Guide</h3>
        <p style={{ color: '#ccc', margin: 0, fontSize: '14px' }}>Let me help you explore the festival!</p>
      </div>

      {conversation.map((msg, index) => (
        <MessageBubble key={index} isUser={msg.sender === 'user'}>
          {msg.sender === 'guide' && <GuideAvatar>🎪</GuideAvatar>}
          <MessageContent className="message-content">
            {msg.message}
          </MessageContent>
        </MessageBubble>
      ))}

      {renderCurrentOptions()}

      <ActionButtons style={{ marginTop: '20px', paddingTop: '15px', borderTop: '1px solid #333' }}>
        <CancelButton onClick={onCancel}>
          Close Guide
        </CancelButton>
        {currentStep !== 'welcome' && (
          <ActionButton onClick={handleStartOver}>
            Start Over
          </ActionButton>
        )}
      </ActionButtons>
    </ConversationContainer>
  );
};

export default FestivalGuideConversation;